import React, {forwardRef, useRef} from 'react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import {
  TooltipProviderProps,
  TooltipProps,
  TooltipTriggerProps,
  TooltipPortalProps,
  TooltipContentProps,
  TooltipArrowProps
} from './types'

/**
 *
 * Wraps your app to provide global functionality to your tooltips.
 *
 */
const Provider = ({children, ...props}: TooltipProviderProps) => {
  return <RadixTooltip.Provider {...props}>{children}</RadixTooltip.Provider>
}

/**
 *
 * Contains all the parts of a tooltip.
 *
 */
const Root = ({children, ...props}: TooltipProps) => {
  return <RadixTooltip.Root {...props}>{children}</RadixTooltip.Root>
}

/**
 *
 * The button that toggles the tooltip. By default, the Tooltip.Content will position itself against the trigger.
 *
 * [data-state]	"closed" | "delayed-open" | "instant-open"
 *
 */
const Trigger = forwardRef(({children, ...props}: TooltipTriggerProps, forwardedRef) => {
  const innerRef = useRef<HTMLButtonElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, innerRef)
  return (
    <RadixTooltip.Trigger ref={innerRef} {...props}>
      {children}
    </RadixTooltip.Trigger>
  )
})

/**
 *
 * The button that toggles the tooltip. By default, the Tooltip.Content will position itself against the trigger.
 *
 * [data-state]	"closed" | "delayed-open" | "instant-open"
 *
 */
const Portal = ({children, ...props}: TooltipPortalProps) => {
  return <RadixTooltip.Portal {...props}>{children}</RadixTooltip.Portal>
}

/**
 *
 * The component that pops out when the tooltip is open.
 *
 * [data-state]	"closed" | "delayed-open" | "instant-open"
 * [data-side]	"left" | "right" | "bottom" | "top"
 * [data-align]	"start" | "end" | "center"
 *
 */
const Content = forwardRef(
  (
    {
      children,
      forceMount = true,
      asChild = false,
      align = 'center',
      side = 'top',
      sideOffset = 0,
      alignOffset = 0,
      avoidCollisions = true,
      collisionBoundary = [],
      collisionPadding = 0,
      arrowPadding = 0,
      sticky = 'partial',
      hideWhenDetached = false,
      'aria-label': ariaLabel,
      testId,
      ...props
    }: TooltipContentProps,
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, innerRef)

    return (
      <RadixTooltip.Content
        ref={innerRef}
        asChild={asChild}
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        avoidCollisions={avoidCollisions}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        arrowPadding={arrowPadding}
        sticky={sticky}
        hideWhenDetached={hideWhenDetached}
        aria-label={ariaLabel}
        data-testid={testId}
        {...props}
      >
        {children}
      </RadixTooltip.Content>
    )
  }
)

/**
 *
 * An optional arrow element to render alongside the tooltip.
 *
 * This can be used to help visually link the trigger with the Tooltip.Content. Must be rendered inside Tooltip.Content.
 *
 */
const Arrow = ({children, ...props}: TooltipArrowProps) => {
  return <RadixTooltip.Arrow {...props}>{children}</RadixTooltip.Arrow>
}

// Exports
Provider.displayName = 'Tooltip.Provider'
Root.displayName = 'Tooltip'
Trigger.displayName = 'Tooltip.Trigger'
Portal.displayName = 'Tooltip.Portal'
Content.displayName = 'Tooltip.Content'
Arrow.displayName = 'Tooltip.Arrow'

/**
 * A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.
 */
export default Object.assign(Root, {Provider, Trigger, Portal, Content, Arrow})
